// UPost.com - API Client
import { API_URL } from '../config';

class APIClient {
  constructor() {
    this.baseURL = `${API_URL}/api`;
    this.token = localStorage.getItem('token');
  }

  setToken(token) {
    this.token = token;
    localStorage.setItem('token', token);
  }

  clearToken() {
    this.token = null;
    localStorage.removeItem('token');
  }

  async request(endpoint, options = {}) {
    const headers = {
      ...options.headers,
    };

    // Don't set Content-Type for FormData (browser will set it with boundary)
    if (!(options.body instanceof FormData)) {
      headers['Content-Type'] = 'application/json';
    }

    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }

    const response = await fetch(`${this.baseURL}${endpoint}`, {
      ...options,
      headers,
    });

    // Read the response body once and store it
    const contentType = response.headers.get('content-type');
    let responseData = null;
    
    if (contentType && contentType.includes('application/json')) {
      try {
        responseData = await response.json();
      } catch (e) {
        // If JSON parsing fails, responseData stays null
        console.error(`JSON parsing failed for ${endpoint}:`, e);
      }
    }
    
    if (!response.ok) {
      let errorMessage = 'Request failed';
      
      // IMPORTANT: Always prefer backend error message (it contains detailed info like remaining attempts)
      if (responseData && responseData.detail) {
        errorMessage = responseData.detail;
      } else if (responseData && responseData.message) {
        errorMessage = responseData.message;
      } else if (responseData && typeof responseData === 'string') {
        errorMessage = responseData;
      } else {
        // Only use fallback messages if backend didn't provide any detail
        if (response.status === 401) {
          errorMessage = 'Your email or password are incorrect. Please try again.';
        } else if (response.status === 402) {
          errorMessage = 'Payment required. Please complete payment to continue.';
        } else if (response.status === 403) {
          errorMessage = 'Access forbidden. Your account may be locked.';
        } else if (response.status === 404) {
          errorMessage = 'Resource not found';
        } else if (response.status >= 500) {
          errorMessage = 'Server error. Please try again later.';
        } else {
          errorMessage = 'Network error. Please check your connection.';
        }
      }
      
      throw new Error(errorMessage);
    }

    return responseData;
  }

  // Auth
  async register(email, password, fullName, referralCode = null) {
    const body = { email, password, full_name: fullName };
    if (referralCode) {
      body.referral_code = referralCode;
    }
    return this.request('/auth/register', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  async login(email, password, twoFactorCode = null) {
    return this.request('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password, two_factor_code: twoFactorCode }),
    });
  }

  async getCurrentUser() {
    return this.request('/auth/me');
  }

  // Documents
  async getDocuments(includeArchived = false) {
    return this.request(`/documents?include_archived=${includeArchived}`);
  }

  async getDocument(id) {
    return this.request(`/documents/${id}`);
  }

  async createDocument(title, docType) {
    return this.request('/documents/', {
      method: 'POST',
      body: JSON.stringify({ title, doc_type: docType }),
    });
  }

  async updateDocument(id, updates) {
    return this.request(`/documents/${id}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  async autosaveDocument(id, content) {
    return this.request(`/documents/${id}/autosave`, {
      method: 'POST',
      body: JSON.stringify({ content }),
    });
  }

  async deleteDocument(id) {
    return this.request(`/documents/${id}`, { method: 'DELETE' });
  }

  async duplicateDocument(id) {
    return this.request(`/documents/${id}/duplicate`, { method: 'POST' });
  }

  // AI
  async generateAI(documentId, prompt, operation, context = null, tone = null) {
    return this.request('/ai/generate', {
      method: 'POST',
      body: JSON.stringify({
        document_id: documentId,
        prompt,
        operation,
        context,
        tone,
        max_tokens: 4000  // Increased to ~3000 words per generation
      }),
    });
  }

  async getAIUsage() {
    return this.request('/ai/usage');
  }

  async bulkGenerateAI(documentId, targetWords, prompt, selectedText = '', selectionStart = 0, selectionEnd = 0) {
    // Use longer timeout for bulk operations (up to 5 minutes)
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 300000); // 5 minutes
    
    try {
      const response = await fetch(`${this.baseURL}/ai/bulk-generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.token}`,
        },
        body: JSON.stringify({
          document_id: documentId,
          target_words: targetWords,
          prompt: prompt,
          selected_text: selectedText,
          selection_start: selectionStart,
          selection_end: selectionEnd
        }),
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      if (!response.ok) {
        const error = await response.json().catch(() => ({ detail: 'Network error' }));
        throw new Error(error.detail || 'Bulk generation failed');
      }
      
      return response.json();
    } catch (error) {
      clearTimeout(timeoutId);
      if (error.name === 'AbortError') {
        throw new Error('Request timeout - bulk generation took too long. Try a smaller target word count.');
      }
      throw error;
    }
  }

  // Security endpoints
  async logCopyAttempt(documentId, attemptType) {
    return this.request('/security/log-copy-attempt', {
      method: 'POST',
      body: JSON.stringify({ document_id: documentId, attempt_type: attemptType })
    });
  }

  async getSecurityLogs(documentId) {
    return this.request(`/security/document/${documentId}/logs`);
  }

  async getDocumentSecuritySettings(documentId) {
    return this.request(`/security/document/${documentId}/settings`);
  }

  async updateDocumentSecuritySettings(documentId, settings) {
    return this.request(`/security/document/${documentId}/settings`, {
      method: 'PUT',
      body: JSON.stringify(settings)
    });
  }
}

export const api = new APIClient();